import { getMaImageUsages, type MaImageUsage } from '@/lib/zybooks-api';
import { useEffect, useState } from 'react';

type FetchState = {
    requestKey: number | null;
    usages: MaImageUsage[];
    loading: boolean;
    error: string | null;
};

const idleState: FetchState = {
    requestKey: null,
    usages: [],
    loading: false,
    error: null,
};

export function useMaImageUsages(maImageId: number | null | undefined, enabled: boolean) {
    const shouldFetch = enabled && typeof maImageId === 'number' && maImageId > 0;
    const requestKey = shouldFetch ? maImageId : null;

    const [fetchState, setFetchState] = useState<FetchState>(idleState);

    useEffect(() => {
        if (requestKey === null) {
            return;
        }

        let cancelled = false;

        void getMaImageUsages(requestKey)
            .then(nextUsages => {
                if (cancelled) {
                    return;
                }
                setFetchState({
                    requestKey,
                    usages: nextUsages,
                    loading: false,
                    error: null,
                });
            })
            .catch(fetchError => {
                if (cancelled) {
                    return;
                }
                setFetchState({
                    requestKey,
                    usages: [],
                    loading: false,
                    error: (fetchError as Error).message ?? 'Failed to load image usages',
                });
            });

        return () => {
            cancelled = true;
        };
    }, [requestKey]);

    if (!shouldFetch) {
        return { usages: [], loading: false, error: null };
    }

    if (fetchState.requestKey !== requestKey) {
        return { usages: [], loading: true, error: null };
    }

    return {
        usages: fetchState.usages,
        loading: fetchState.loading,
        error: fetchState.error,
    };
}
